import {
  GenericSectionError,
  LoadingSkeleton,
  TitleSection,
  SuccessAlert,
  ErrorAlert,
} from 'components';
import { ItemProduct } from 'components/molecules/Item-product/Item-product';
import { useNavigate, useParams } from 'react-router';
import { ProductsDetailsView } from 'shared/services/user-subscription';
import { useState, useEffect } from 'react';
import { AddonSelectionModal } from 'components/molecules/Item-subscription/components/AddonSelectionModal';
import { useSelector } from 'react-redux';
import { IAppReducer } from 'models';
import { getProductsByCompany } from 'shared/services/user-add-subscription';
import { getAddonsConfig } from 'shared/services/settings.service';
import { Product } from 'pages/Settings/pages/Add-subscription/types/Add-subscription.types';
import { usePricesNotSpecial } from 'hooks/usePricesNotSpecial';

interface ProductsListProps {
  products: ProductsDetailsView[];
  error: boolean;
  loading: boolean;
  customerStripeId: string;
  providerProcessor: string;
  subscriptionAmount: number;
  subscriptionPeriod: 'Mensual' | 'Anual';
  subscriptionDateExpire: string;
  onRefreshProducts?: () => void;
  dbId: number;
  isInsurance?: boolean;
}

export const ProductsList = ({
  products,
  error,
  loading,
  customerStripeId,
  providerProcessor,
  subscriptionAmount,
  subscriptionPeriod,
  subscriptionDateExpire,
  onRefreshProducts,
  dbId,
  isInsurance = false,
}: ProductsListProps) => {
  const navigate = useNavigate();
  const { subId, subVersion } = useParams();
  const { authReducer: user } = useSelector((state: IAppReducer) => state);
  const isTester = user?.agentAuthData?.isTester ?? false;
  const { hasSpecialPrice } = usePricesNotSpecial();
  const [showAddonModal, setShowAddonModal] = useState(false);
  const [availableProducts, setAvailableProducts] = useState<Product[]>([]);
  const [selectedProduct, setSelectedProduct] =
    useState<ProductsDetailsView | null>(null);
  const [successAlert, setSuccessAlert] = useState({
    show: false,
    title: '',
    message: '',
  });
  const [errorAlert, setErrorAlert] = useState({
    show: false,
    title: '',
    message: '',
  });

  useEffect(() => {
    const loadProducts = async () => {
      try {
        // Load addons config and products in parallel
        const [addonsConfig, products] = await Promise.all([
          getAddonsConfig(),
          getProductsByCompany(user.companyId, user.agentAuthData?.email),
        ]);

        const productsWithoutPrices = products.filter(
          (product) =>
            !product.prices.some((price) =>
              addonsConfig.includes(price.stripe_price_id)
            )
        );
        setAvailableProducts(productsWithoutPrices);
      } catch (error) {
        console.error('Error loading products:', error);
        try {
          const products = await getProductsByCompany(
            user.companyId,
            user.agentAuthData?.email
          );
          setAvailableProducts(products);
        } catch (fallbackError) {
          console.error('Error loading products fallback:', fallbackError);
        }
      }
    };

    if (user.companyId) {
      loadProducts();
    }
  }, [user.companyId]);

  const productsDetails = (products || []).map((product) => ({
    type: (product.type || '').toLowerCase(),
    isAddon: product.isAddon || false,
    quantity: product.quantity || 0,
    calculateAmount: product.calculateAmount ?? true, // Si no existe, se asume que es true
    amount: product.amount || 0,
  }));

  const isPlanMigratedPriceSpecialNew = (): boolean => {
    const nonAddonProducts = productsDetails.filter(
      (product) => !product.isAddon
    );
    return nonAddonProducts.some((product) => hasSpecialPrice(product.amount));
  };

  const handleEditClick = (productId: number) => {
    const isSpecialPrice = isPlanMigratedPriceSpecialNew();

    if (isSpecialPrice) {
      navigate(`/edit-plan/${dbId}/${subId}?isPriceSpecial=true`);
    } else {
      navigate(`/settings/subscriptions/edit/${subId}/version/${subVersion}`);
    }
  };

  const canProductHaveAddons = (productType: string) => {
    if (!availableProducts || availableProducts.length === 0) {
      return false;
    }

    const hasAddonsForType = availableProducts.some((product) => {
      if (!product.is_addon) return false;

      return product.prices.some((price) => {
        const addonType = price.product_type_id.name.toLowerCase();
        const addonPeriod = price.periocity_id.name.toLowerCase();
        const currentPeriod = subscriptionPeriod.toLowerCase();

        return (
          addonType === productType.toLowerCase() &&
          addonPeriod === currentPeriod
        );
      });
    });

    return hasAddonsForType;
  };

  const handleAddAddon = (product: ProductsDetailsView) => {
    setSelectedProduct(product);
    setShowAddonModal(true);
  };

  const handleCloseAddonModal = () => {
    setShowAddonModal(false);
    setSelectedProduct(null);
  };

  // Funciones para manejar alerts
  const showSuccessAlert = (message: string, title = '¡Éxito!') => {
    setSuccessAlert({
      show: true,
      title,
      message,
    });
  };

  const showErrorAlert = (message: string, title = 'Error') => {
    setErrorAlert({
      show: true,
      title,
      message,
    });
  };

  const hideSuccessAlert = () => {
    setSuccessAlert((prev) => ({ ...prev, show: false }));
  };

  const hideErrorAlert = () => {
    setErrorAlert((prev) => ({ ...prev, show: false }));
  };

  const handleConfirmAddons = async () => {
    setShowAddonModal(false);
    setSelectedProduct(null);

    showSuccessAlert('Los addons se agregaron exitosamente');

    // Recargar los productos
    if (onRefreshProducts) {
      onRefreshProducts();
    }
  };

  const handleAddonError = (error: string) => {
    showErrorAlert(error);
  };

  return (
    <>
      {/* Alerts */}
      {successAlert.show && (
        <SuccessAlert
          isOpen={successAlert.show}
          title={successAlert.title}
          message={successAlert.message}
          onClose={hideSuccessAlert}
        />
      )}
      {errorAlert.show && (
        <ErrorAlert
          isOpen={errorAlert.show}
          title={errorAlert.title}
          message={errorAlert.message}
          onClose={hideErrorAlert}
        />
      )}

      <TitleSection title="Productos"></TitleSection>
      <div className="subscription_details__content">
        <div className="subscription_details__content__items">
          {error ? (
            <GenericSectionError template="tertiary" />
          ) : loading ? (
            <LoadingSkeleton count={2} />
          ) : !products?.length ? (
            <span>No tienes products</span>
          ) : (
            products.map((product) => (
              <ItemProduct
                userProductIdPublic={product.userProductIdPublic}
                products={products}
                key={product.productId}
                providerProcessor={providerProcessor}
                customerStripeId={customerStripeId}
                productId={product.productId}
                productTitle={product.productName}
                quantity={product.quantity}
                hasTrial={product.hasTrial}
                price={product.amount}
                status={product.status}
                aliasProductType={product.aliasProductType}
                handleClickEdit={() => handleEditClick(product.productId)}
                onClickAddAddOns={
                  !product.isAddon &&
                  !isInsurance &&
                  canProductHaveAddons(product.type)
                    ? () => {
                        navigate(
                          `/settings/subscriptions/edit/${subId}/version/${subVersion}`
                        );
                      }
                    : undefined
                }
                isInsurance={isInsurance}
              />
            ))
          )}
        </div>
      </div>

      {/* Modal para seleccionar addons */}
      <AddonSelectionModal
        isOpen={showAddonModal}
        onClose={handleCloseAddonModal}
        onConfirm={handleConfirmAddons}
        onError={handleAddonError}
        productsDetails={productsDetails}
        period={subscriptionPeriod}
        isLoading={false}
        dateExpire={subscriptionDateExpire}
        typePeriodicity={subscriptionPeriod === 'Mensual' ? 'MENSUAL' : 'ANUAL'}
        subId={subId || ''}
        currentPlanPrice={subscriptionAmount}
        products={availableProducts}
        selectedProductQuantity={selectedProduct?.quantity || 0}
        selectedProductType={selectedProduct?.type || ''}
        selectedUserProductId={selectedProduct?.productId || 0}
      />
    </>
  );
};
